"use client"

import * as React from "react"
import { ChevronDown } from "lucide-react"
import { parsePhoneNumberFromString } from "libphonenumber-js/min"

import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  TOP_COUNTRIES,
  flagEmoji,
  formatAsYouType,
  getDialCode,
  listAllCountries,
  type CountryCode,
} from "@/lib/phone"

export interface PhoneInputLabels {
  countrySelect?: string
  countrySearch?: string
  countryEmpty?: string
  inputPlaceholder?: string
}

export interface PhoneInputMeta {
  isValid: boolean
  country: CountryCode
}

export interface PhoneInputProps {
  value: string
  onChange: (e164OrRaw: string, meta: PhoneInputMeta) => void
  onBlur?: () => void
  defaultCountry?: CountryCode
  nameLocale?: string
  labels?: PhoneInputLabels
  id?: string
  name?: string
  disabled?: boolean
  invalid?: boolean
  required?: boolean
  className?: string
  inputClassName?: string
  "aria-describedby"?: string
  "aria-invalid"?: boolean
}

interface CountryEntry {
  code: CountryCode
  dial: string
  name: string
}

function useCountryEntries(nameLocale: string): {
  top: CountryEntry[]
  rest: CountryEntry[]
  byCode: Map<CountryCode, CountryEntry>
} {
  return React.useMemo(() => {
    const displayNames = (() => {
      try {
        return new Intl.DisplayNames([nameLocale], { type: "region" })
      } catch {
        return new Intl.DisplayNames(["en"], { type: "region" })
      }
    })()
    const all = listAllCountries()
    const byCode = new Map<CountryCode, CountryEntry>()
    for (const code of all) {
      const name = displayNames.of(code) ?? code
      byCode.set(code, { code, name, dial: getDialCode(code) })
    }
    const top = TOP_COUNTRIES.map((c) => byCode.get(c)).filter(
      (x): x is CountryEntry => Boolean(x),
    )
    const topSet = new Set(TOP_COUNTRIES)
    const rest = [...byCode.values()]
      .filter((c) => !topSet.has(c.code))
      .sort((a, b) => a.name.localeCompare(b.name))
    return { top, rest, byCode }
  }, [nameLocale])
}

export function PhoneInput({
  value,
  onChange,
  onBlur,
  defaultCountry = "BR",
  nameLocale = "pt-BR",
  labels,
  id,
  name,
  disabled,
  invalid,
  required,
  className,
  inputClassName,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
}: PhoneInputProps) {
  const entries = useCountryEntries(nameLocale)
  const listboxId = React.useId()

  // Internal state: selected country + user-visible national format.
  const [country, setCountry] = React.useState<CountryCode>(defaultCountry)
  const [national, setNational] = React.useState("")
  const [open, setOpen] = React.useState(false)
  const lastEmittedRef = React.useRef<string>("")

  // Sync from external value changes (e.g. parent reset).
  React.useEffect(() => {
    if (value === lastEmittedRef.current) return
    if (!value) {
      setNational("")
      return
    }
    const parsed = parsePhoneNumberFromString(value)
    if (parsed?.country) {
      setCountry(parsed.country)
      setNational(parsed.formatNational())
    } else {
      // Unparseable legacy value — keep raw in the local input, country stays as-is.
      setNational(value)
    }
  }, [value])

  const emit = React.useCallback(
    (nextCountry: CountryCode, nextNational: string) => {
      const trimmed = nextNational.trim()
      if (!trimmed) {
        lastEmittedRef.current = ""
        onChange("", { isValid: false, country: nextCountry })
        return
      }
      const parsed = parsePhoneNumberFromString(trimmed, nextCountry)
      if (parsed?.isValid()) {
        lastEmittedRef.current = parsed.number
        onChange(parsed.number, {
          isValid: true,
          country: parsed.country ?? nextCountry,
        })
        return
      }
      // Not a full valid number yet — emit the partial value so parent can still
      // detect "not empty" but mark as invalid.
      lastEmittedRef.current = trimmed
      onChange(trimmed, { isValid: false, country: nextCountry })
    },
    [onChange],
  )

  function handleNationalChange(raw: string) {
    // If the user pastes/types a leading "+", reinterpret as international and
    // switch the country automatically.
    const trimmed = raw.trim()
    if (trimmed.startsWith("+")) {
      const parsed = parsePhoneNumberFromString(trimmed)
      if (parsed?.country) {
        setCountry(parsed.country)
        const formatted = parsed.formatNational()
        setNational(formatted)
        emit(parsed.country, formatted)
        return
      }
      // Keep typing — show raw with the +.
      setNational(trimmed)
      emit(country, trimmed)
      return
    }

    const formatted = formatAsYouType(raw, country)
    setNational(formatted)
    emit(country, formatted)
  }

  function handleCountryChange(code: CountryCode) {
    setCountry(code)
    // Re-format current national digits for the newly selected country.
    if (national) {
      const formatted = formatAsYouType(national, code)
      setNational(formatted)
      emit(code, formatted)
    } else {
      emit(code, "")
    }
    setOpen(false)
  }

  const selectedEntry =
    entries.byCode.get(country) ??
    entries.top[0] ??
    entries.rest[0]

  const triggerLabel = (
    <span className="flex items-center gap-1.5">
      <span aria-hidden>{flagEmoji(country)}</span>
      <span className="text-xs text-muted-foreground">
        {selectedEntry?.dial}
      </span>
    </span>
  )

  return (
    <div className={cn("flex w-full gap-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-controls={listboxId}
            aria-expanded={open}
            aria-label={labels?.countrySelect ?? "País"}
            disabled={disabled}
            className={cn(
              "inline-flex h-9 shrink-0 items-center justify-between gap-1 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
              invalid && "border-red-500",
            )}
            style={{ borderWidth: "0.5px", borderColor: invalid ? undefined : "#CBD5E1" }}
          >
            {triggerLabel}
            <ChevronDown className="size-4 opacity-50" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[280px] p-0"
          align="start"
        >
          <Command
            filter={(itemValue, search) => {
              const haystack = itemValue.toLowerCase()
              const needle = search.toLowerCase().trim()
              return haystack.includes(needle) ? 1 : 0
            }}
          >
            <CommandInput
              placeholder={labels?.countrySearch ?? "Buscar país..."}
            />
            <CommandList id={listboxId}>
              <CommandEmpty>
                {labels?.countryEmpty ?? "Nenhum país encontrado."}
              </CommandEmpty>
              <CommandGroup>
                {entries.top.map((entry) => (
                  <CommandItem
                    key={entry.code}
                    value={`${entry.name} ${entry.dial} ${entry.code}`}
                    onSelect={() => handleCountryChange(entry.code)}
                    className="[&>svg.ml-auto]:hidden"
                  >
                    <span className="flex w-full items-center gap-2">
                      <span aria-hidden>{flagEmoji(entry.code)}</span>
                      <span className="flex-1 truncate">{entry.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {entry.dial}
                      </span>
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                {entries.rest.map((entry) => (
                  <CommandItem
                    key={entry.code}
                    value={`${entry.name} ${entry.dial} ${entry.code}`}
                    onSelect={() => handleCountryChange(entry.code)}
                    className="[&>svg.ml-auto]:hidden"
                  >
                    <span className="flex w-full items-center gap-2">
                      <span aria-hidden>{flagEmoji(entry.code)}</span>
                      <span className="flex-1 truncate">{entry.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {entry.dial}
                      </span>
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Input
        id={id}
        name={name}
        type="tel"
        autoComplete="tel-national"
        inputMode="tel"
        disabled={disabled}
        required={required}
        value={national}
        placeholder={labels?.inputPlaceholder}
        onChange={(e) => handleNationalChange(e.target.value)}
        onBlur={onBlur}
        className={cn("flex-1", inputClassName)}
        aria-invalid={ariaInvalid ?? invalid ? true : undefined}
        aria-describedby={ariaDescribedBy}
        style={{
          borderWidth: "0.5px",
          borderColor: invalid ? "#EF4444" : "#CBD5E1",
        }}
      />
    </div>
  )
}
